import React, { useMemo, useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { notification, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { debounce, ceil, trim, capitalize } from 'lodash';

import Search from '../../../components/uiComponent/inputs/searchinput';
import Button from '../../../components/uiComponent/button';
import Modal from "../../../components/uiComponent/modal/index";
import AgGrid from "../../../components/ag-grid-table/index";
import Input from "../../../components/uiComponent/inputs/input/index";
import Pagination from '../../../components/pagination';
import Select from "../../../components/uiComponent/select/index";

import WarningIcon from '../../../assets/icons/warning-modal-icon.svg';
import SideDrawerIcon from "../../../assets/icons/side-bar-icon.svg";

import { PoHistoryWrapper, ModalWrapper } from './style';
import useResponsiveTableHeight from '../../../hooks/useResponsiveTableHeight';
import AddManualProduct from '../po-details/add-Product/addProductManual';
import { CreatePo, GetPoListing, GetVendorsList, DeletePo } from '../../../slices/po';

const defaultColDef = {
  resizable: true,
  sortable: false,
  suppressMenu: true,
  suppressMovable: true,
  suppressSorting: true,
};

const getStatusBadgeClass = (status) => {
  if (!status) return '';
  const s = status.toLowerCase();
  if (s === 'pending') return 'badge-pending';
  if (s === 'confirmed') return 'badge-confirmed';
  if (s === 'arrived') return 'badge-arrived';
  if (s === 'receiving') return 'badge-receiving';
  if (s === 'processed') return 'badge-processed';
  if (s === 'deleted') return 'badge-deleted';
  return '';
};

let gridColumnApi;

const PoHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '', poStatus: '', vendor: '' });
  const [sortBy, setSortBy] = useState({ key: '', type: '' });
  const [addProduct, setAddProduct] = useState();
  const [showCreatePoModal, setShowCreatePoModal] = useState(false);
  const [createPoData, setCreatePoData] = useState({ vendorName: '', poName: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const [poId, setPoId] = useState(null);
  const { tableHeight, tableSectionRef, paginationRef } = useResponsiveTableHeight({
    minHeight: 220,
    bottomSpacing: 24,
    observeSelectors: ['.box-row', '.po-toolbar']
  });

  const {
    pos,
    totalPosCount,
    fetchingPos,
    vendorsList,
    fetchingVendorsList,
    creatingPo,
    deletingPo,
    userPoAllStatusCount
  } = useSelector((state) => state.pos || {});

  useEffect(() => {
    dispatch(GetPoListing({ page, filters, sortBy }));
    dispatch(GetVendorsList());
  }, []);

  const handleClose = () => setIsModalOpen(false);

  // Stat cards data
  const boxData = [
    { label: 'Total POs', value: totalPosCount || 0, className: 'box-purple', key: null },
    { label: 'Pending', value: userPoAllStatusCount?.['Pending'] || 0, className: 'box-orange', key: 'Pending' },
    { label: 'Confirmed', value: userPoAllStatusCount?.['Confirmed'] || 0, className: 'box-blue', key: 'Confirmed' },
    { label: 'Arrived', value: userPoAllStatusCount?.['Arrived'] || 0, className: 'box-green', key: 'Arrived' },
    { label: 'Receiving', value: userPoAllStatusCount?.['Receiving'] || 0, className: 'box-cyan', key: 'Receiving' },
    { label: 'Processed', value: userPoAllStatusCount?.['Processed'] || 0, className: 'box-violet', key: 'Processed' },
    { label: 'Deleted', value: userPoAllStatusCount?.['Deleted'] || 0, className: 'box-red', key: 'Deleted' },
  ];

  const handleCardFilter = (key) => {
    setActiveStatus(key);
    handleFilterChange({ key: 'poStatus', value: key });
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Date',
      field: 'created_at',
      minWidth: 110,
      sortable: true,
      comparator: () => 0,
      cellRenderer: ({ value }) => {
        if (!value) return '—';
        const date = new Date(value);
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
      },
    },
    {
      headerName: 'Vendor',
      minWidth: 130,
      flex: 1,
      field: 'vendor.name',
      cellRenderer: ({ value }) => value || '—'
    },
    {
      headerName: 'PO Name',
      minWidth: 200,
      field: 'file_name',
      flex: 1.5,
      cellRenderer: ({ value }) => value || '—'
    },
    {
      headerName: 'Status',
      minWidth: 120,
      field: 'status',
      cellRendererFramework: ({ value }) => {
        if (!value) return '—';
        return <span className={`po-status-badge ${getStatusBadgeClass(value)}`}>{capitalize(value)}</span>;
      },
    },
    {
      headerName: 'Ordered Qty',
      minWidth: 110,
      field: 'ordered_quantity',
      comparator: () => 0,
      sortable: true,
      valueFormatter: (params) => Number(params?.value || 0)?.toLocaleString('en-US'),
    },
    {
      headerName: 'Invoice Qty',
      minWidth: 110,
      field: 'invoice_quantity',
      comparator: () => 0,
      sortable: true,
      valueFormatter: (params) => Number(params?.value || 0)?.toLocaleString('en-US'),
    },
    {
      headerName: 'Received Qty',
      minWidth: 110,
      field: 'received_quantity',
      comparator: () => 0,
      sortable: true,
      valueFormatter: (params) => Number(params?.value || 0)?.toLocaleString('en-US'),
    },
    {
      headerName: 'Missing',
      minWidth: 100,
      field: 'missing_units',
      sortable: true,
      comparator: () => 0,
      cellRenderer: ({ data }) => (data?.missing_units || 0)?.toLocaleString('en-US'),
    },
    {
      headerName: 'PO Value (Order)',
      field: 'po_by_order_quantity',
      minWidth: 120,
      comparator: () => 0,
      sortable: true,
      cellRenderer: ({ value = 0 }) => `$${Number(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      headerName: 'PO Value (Invoice)',
      field: 'po_value',
      minWidth: 120,
      comparator: () => 0,
      sortable: true,
      cellRenderer: ({ value = 0 }) => `$${Number(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      headerName: 'Action',
      field: 'action',
      minWidth: 76,
      width: 76,
      pinned: 'right',
      headerClass: 'ag-center-aligned-header',
      headerStyle: { textAlign: 'center' },
      cellRendererFramework: ({ data }) => (
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ height: '100%' }}>
          {data?.status?.toLowerCase() !== 'deleted' && (
            <Tooltip title="Edit PO">
              <span style={{ cursor: 'pointer', color: '#1565D8' }} onClick={() => handleNavigateEditPo(data?.id)}>
                <FiEdit2 size={14} />
              </span>
            </Tooltip>
          )}
          <Tooltip title={data?.status?.toLowerCase() !== 'pending' ? 'Only pending POs can be deleted' : 'Delete PO'}>
            <span
              style={{
                cursor: data?.status?.toLowerCase() === 'pending' ? 'pointer' : 'not-allowed',
                color: data?.status?.toLowerCase() === 'pending' ? '#D95040' : '#ccc'
              }}
              onClick={data?.status?.toLowerCase() === 'pending' ? () => { setIsModalOpen(true); setPoId(data?.id); } : undefined}
            >
              <FiTrash2 size={14} />
            </span>
          </Tooltip>
        </div>
      ),
    },
  ], []);

  const handleSearchPo = debounce((value) => {
    if (trim(value) || filters.search) {
      setFilters(filters => ({ ...filters, search: trim(value) }));
      setPage(page => ({ ...page, pageNumber: 1 }));
      dispatch(GetPoListing({
        page: { ...page, pageNumber: 1 },
        filters: { ...filters, search: trim(value) },
        sortBy
      }));
    }
  }, 800);

  const handlePagination = (number, key) => {
    const value = Number(number);
    let newPage = {};
    if (key === 'pageSize') newPage = { pageNumber: 1, pageSize: value };
    else newPage = { ...page, pageNumber: value };
    setPage(newPage);
    dispatch(GetPoListing({ page: newPage, filters, sortBy }));
  };

  const gridReady = (params) => { gridColumnApi = params.columnApi; };

  const refreshGridClick = () => {
    if (gridColumnApi) {
      gridColumnApi.applyColumnState({ defaultState: { sort: null }, applyOrder: true });
      setSortBy({ key: '', type: '' });
    }
    dispatch(GetPoListing({ page, filters }));
  };

  const handleNavigateEditPo = (id) => navigate(`/po-details/${id}`);

  const { pageNumber, pageSize } = page || {};

  const handleFilterChange = ({ key, value }) => {
    setFilters(filters => ({ ...filters, [key]: value }));
    setPage(page => ({ ...page, pageNumber: 1 }));
    dispatch(GetPoListing({
      page: { ...page, pageNumber: 1 },
      filters: { ...filters, [key]: value },
      sortBy
    }));
  };

  const onSortChange = (params) => {
    const value = params.columnApi.getColumnState().find(s => s.sort != null);
    if (value && ['ordered_quantity', 'received_quantity', 'po_value', 'po_by_order_quantity', 'total_skus', 'total_products', 'invoice_quantity', 'created_at', 'missing_units'].includes(value?.colId)) {
      const { colId, sort } = value || {};
      setSortBy({ key: colId, type: sort });
      dispatch(GetPoListing({ page, filters, sortBy: { key: colId, type: sort } }));
    }
  };

  const handleCloseCreatePoModal = () => {
    setShowCreatePoModal(false);
    setCreatePoData({ poName: '', vendorName: '' });
  };

  const handleCreatePo = () => {
    const matchedEntries = pos.filter(({ file_name }) => file_name === createPoData.poName);
    if (matchedEntries?.length) {
      return notification.error({ top: 65, message: 'Create PO', description: 'PO name must be unique' });
    }
    dispatch(CreatePo({ ...createPoData })).then(({ payload }) => {
      if (payload?.success) handleCloseCreatePoModal();
      refreshGridClick();
    });
  };

  return (
    <PoHistoryWrapper>
      {/* Stat Cards */}
      <div className="box-row">
        {boxData.map((data, idx) => (
          <div
            key={idx}
            className={`sku-box ${data.className} ${activeStatus === data.key ? 'active-box' : ''}`}
            onClick={() => handleCardFilter(data.key)}
          >
            <p>{data.label}</p>
            <h4>{data.value}</h4>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="po-toolbar">
        <div className="toolbar-left">
          <h5 className="toolbar-title">Purchase Orders</h5>
          <span className="toolbar-count">{totalPosCount || 0}</span>
        </div>
        <div className="toolbar-right">
          <Search
            className="toolbar-search"
            placeholder="Search PO name, UPC..."
            onChange={({ target }) => handleSearchPo(target.value)}
          />
          <Select
            isClearable={true}
            placeholder="Vendor"
            className="mb-0"
            width="170px"
            loading={fetchingVendorsList}
            options={vendorsList}
            onChange={(e) => handleFilterChange({ key: 'vendor', value: e?.value })}
          />
          <Button onClick={() => setShowCreatePoModal(true)} className="primary mb-0" startIcon={<FiPlus size={14} />} text="Create PO" />
        </div>
      </div>

      {/* Table */}
      <div ref={tableSectionRef}>
        <Spin tip='Loading...' spinning={fetchingPos}>
          <AgGrid
            gridReady={gridReady}
            user_referrals={pos}
            defaultColDef={defaultColDef}
            columnDefinitions={columnDefs}
            rowHeight={52}
            headerHeight={34}
            containerHeight={tableHeight}
            masterDetail={false}
            detailRowAutoHeight={false}
            onSortChange={onSortChange}
            className="sku-table"
          />
        </Spin>
      </div>

      {/* Pagination */}
      <div ref={paginationRef}>
        <Pagination
          total={totalPosCount}
          totalPages={ceil(totalPosCount / pageSize + 1)}
          pageNumber={pageNumber}
          pageLimit={pageSize}
          handleLimitChange={(e) => handlePagination(e, 'pageSize')}
          handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
          handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
          refreshGridClick={refreshGridClick}
        />
      </div>

      {/* File Upload PO */}
      <AddManualProduct refreshGridClick={refreshGridClick} addProduct={addProduct} setAddProduct={setAddProduct} />

      {/* Create PO Modal */}
      <Modal
        open={showCreatePoModal}
        onClose={handleCloseCreatePoModal}
        onHide={handleCloseCreatePoModal}
        closeText="Cancel"
        saveText="Create"
        header={true}
        heading="Create Purchase Order"
        className="po-details-create-header"
        closeButton={true}
        onSave={handleCreatePo}
        buttonDisabled={!createPoData?.poName || !createPoData?.vendorName || creatingPo}
      >
        <Spin tip='Loading...' spinning={creatingPo}>
          <div className='crate-po-wrapper'>
            <Select
              isClearable={true}
              label="Vendor"
              important
              placeholder="Select Vendor"
              className="mb-0"
              loading={fetchingVendorsList}
              options={vendorsList}
              onChange={(e) => {
                const vendorName = e?.value;
                let poName = '';
                if (vendorName) {
                  poName = `${vendorName}-${dayjs().format('DD-MM-YYYY')}`;
                  const matchedEntries = pos.filter(({ file_name }) => file_name.includes(poName));
                  poName += `-${(matchedEntries?.length || 0) + 1}`;
                }
                setCreatePoData({ vendorName, poName });
              }}
            />
            <Input
              height="32px"
              label="PO Name"
              important
              value={createPoData?.poName}
              placeholder="PO Name"
              onChange={({ target }) => setCreatePoData(poData => ({ ...poData, poName: target?.value }))}
            />
          </div>
        </Spin>
      </Modal>

      {/* Delete PO Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        onHide={handleClose}
        buttonDisabled={deletingPo}
        closeText='Cancel'
        saveText='Delete'
        header={true}
        heading='Delete Purchase Order'
        className='delete-confirmation-modal'
        closeButton={true}
        onSave={() => {
          dispatch(DeletePo(poId)).then(({ payload }) => {
            if (payload?.success) setIsModalOpen(false);
            // Refetch in place — deleting a PO must not clear the applied column sort
            // (refreshGridClick is the refresh button's full reset, wrong here).
            dispatch(GetPoListing({ page, filters, sortBy }));
          });
        }}
      >
        <ModalWrapper>
          <div className='delete-modal-wrapper'>
            <img src={WarningIcon} alt='Warning' title='Warning' />
            <h3>Delete PO</h3>
            <p>Do you want to delete this purchase order?</p>
          </div>
        </ModalWrapper>
      </Modal>
    </PoHistoryWrapper>
  );
};

export default PoHistory;
